import { type FC } from "react";
import { Suspense as ReactSuspense } from "react";
import { Navigate } from "react-router-dom";
import { AccessLevelE, UserRoleE } from "../models/enums";
import { RoutesE } from "./routesData";
import Loader from "../components/Loader/Loader";
// ChunkLoadError-resilient React.lazy (retry + one-shot reload on stale deploy).
import { lazyWithRetry } from "../utils/helpers/lazyWithRetry";

// --- Eager (kept in the main chunk on purpose) ---------------------------------
// Primary post-login landing pages + trivial redirect shims. Code-splitting a page
// that renders immediately after login only adds a Loader flash with no bundle win.
import ProjectList from "../pages/projects/ProjectList/ProjectList";
import CampaignsList from "../pages/CampaignList";
import DeliveryServiceIndex from "../pages/DeliveryServiceIndex/DeliveryServiceIndex";
import ViewVideoProjectRedirect from "../pages/ViewVideoProjectRedirect/ViewVideoProjectRedirect";
import CampaignRedirect from "../pages/CampaignRedirect/CampaignRedirect";
import LegacyVideoReportRedirect from "./LegacyVideoReportRedirect";

// --- Lazy (split out of the main bundle — FE-7) --------------------------------
// IMPORTANT: import each page from its OWN module, never from a barrel
// (e.g. "../pages/manage" / "../pages/settings"). A barrel import drags every
// member into the main chunk and silently defeats the code-split for all of them.

// manage/
const Users: FC = lazyWithRetry(() => import("../pages/manage/Users/Users"));
const ServicePackages: FC = lazyWithRetry(
  () => import("../pages/manage/ServicePackages/ServicePackages")
);
const SupportMessages: FC = lazyWithRetry(
  () => import("../pages/manage/SupportMessages/SupportMessages")
);
const Projects: FC = lazyWithRetry(() => import("../pages/manage/Projects/Projects"));
const ProductionCompanies: FC = lazyWithRetry(
  () => import("../pages/manage/ProductionCompanies/ProductionCompanies")
);
const MailTemplates: FC = lazyWithRetry(
  () => import("../pages/manage/MailTemplates/MailTemplates")
);
const Mails: FC = lazyWithRetry(() => import("../pages/manage/Mails/Mails"));
const Industry: FC = lazyWithRetry(() => import("../pages/manage/Industry/Industry"));
const FTPSettings: FC = lazyWithRetry(
  () => import("../pages/manage/FTPSettings/FTPSettings")
);
const DeliveryOrders: FC = lazyWithRetry(
  () => import("../pages/manage/DeliveryOrders/DeliveryOrders")
);
const ReDeliveryOrders: FC = lazyWithRetry(
  () => import("../pages/manage/ReDeliveryOrders/ReDeliveryOrders")
);
const Clients: FC = lazyWithRetry(() => import("../pages/manage/Clients/Clients"));
const Brands: FC = lazyWithRetry(() => import("../pages/manage/Brands/Brands"));
const Advertisers: FC = lazyWithRetry(
  () => import("../pages/manage/Advertisers/Advertisers")
);
const AudioReport: FC = lazyWithRetry(
  () => import("../pages/manage/AudioReport/AudioReport")
);
const ProjectFileBroadcasters: FC = lazyWithRetry(
  () =>
    import("../pages/manage/ProjectFileBroadcasters/ProjectFileBroadcasters")
);
const VideoReport = lazyWithRetry(
  () => import("../pages/manage/VideoReport/VideoReport")
);
const Campaigns: FC = lazyWithRetry(() => import("../pages/manage/Campaigns/Campaigns"));
const Versions: FC = lazyWithRetry(() => import("../pages/manage/Versions/Versions"));
const Translations: FC = lazyWithRetry(
  () => import("../pages/manage/Translations/Translations")
);
const ClientPage: FC = lazyWithRetry(
  () => import("../pages/manage/ClientPage/ClientPage")
);
const Leads: FC = lazyWithRetry(() => import("../pages/manage/Leads/Leads"));
const VideoGuides: FC = lazyWithRetry(() => import("../pages/manage/VideoGuides"));
const NewServicePackages: FC = lazyWithRetry(
  () =>
    import(
      "../features/NewPrices2024/pages/NewServicePackages/NewServicePackages"
    )
);
const ManageGlobalSettings: FC = lazyWithRetry(
  () => import("../features/NewPrices2024/pages/ManageGlobalSettings")
);

// settings/
const SettingsRouteEntry: FC = lazyWithRetry(
  () => import("../pages/settings/SettingsRouteEntry")
);
const UserSettings: FC = lazyWithRetry(
  () => import("../pages/settings/UserSettings/UserSettings")
);
const NotificationSettings: FC = lazyWithRetry(
  () => import("../pages/settings/NotificationSettings/NotificationSettings")
);
const PasswordSettings: FC = lazyWithRetry(
  () => import("../pages/settings/PasswordSettings/PasswordSettings")
);
const CompanyProfileSettings: FC = lazyWithRetry(
  () => import("../pages/settings/CompanyProfileSettings/CompanyProfileSettings")
);
const UserManagement: FC = lazyWithRetry(
  () => import("../pages/settings/UserManagement/UserManagement")
);
const RecipientsManagement: FC = lazyWithRetry(
  () => import("../pages/settings/RecipientsManagement/RecipientsManagement")
);
const FTPSettingsBroadcaster: FC = lazyWithRetry(
  () =>
    import("../pages/settings/FTPSettingsBroadcaster/FTPSettingsBroadcaster")
);

// projects/ + top-level pages
const ViewVideoProject: FC = lazyWithRetry(
  () => import("../pages/projects/ViewVideoProject/ViewVideoProject")
);
const ShareVideo: FC = lazyWithRetry(() => import("../pages/ShareVideo/ShareVideo"));
const ShareVideoAd: FC = lazyWithRetry(
  () => import("../pages/ShareVideoAd/ShareVideoAd")
);
const Campaign: FC = lazyWithRetry(() => import("../pages/Campaign/Campaign"));
const OrderPayment: FC = lazyWithRetry(
  () => import("../pages/OrderPayment/OrderPayment")
);
const PaymentSuccess: FC = lazyWithRetry(
  () => import("../pages/PaymentSuccess/PaymentSuccess")
);
const ReDeliveryConfirmation: FC = lazyWithRetry(
  () => import("../pages/ReDeliveryConfirmation/ReDeliveryConfirmation")
);
const ReDeliveryVersions: FC = lazyWithRetry(
  () => import("../pages/ReDeliveryVersions/ReDeliveryVersions")
);
const ReDeliveryOrderPayment: FC = lazyWithRetry(
  () => import("../pages/ReDeliveryOrderPayment/ReDeliveryOrderPayment")
);
const Support: FC = lazyWithRetry(() => import("../pages/Support/Support"));
const SupportVideoGuides: FC = lazyWithRetry(
  () => import("../pages/SupportVideoGuides")
);
const SupportTechInstruction: FC = lazyWithRetry(
  () => import("../pages/SupportTechInstruction")
);
const HelpMobile: FC = lazyWithRetry(() => import("../pages/HelpMobile/HelpMobile"));
const Terms: FC = lazyWithRetry(() => import("../pages/Terms/Terms"));
const PrivacyPolicy: FC = lazyWithRetry(
  () => import("../pages/PrivacyPolicy/PrivacyPolicy")
);
const Registration: FC = lazyWithRetry(
  () => import("../pages/Registration/Registration")
);
const ForgotPassword: FC = lazyWithRetry(
  () => import("../pages/ForgotPassword/ForgotPassword")
);
const ResetPassword: FC = lazyWithRetry(
  () => import("../pages/ResetPassword/ResetPassword")
);
const SearchResults: FC = lazyWithRetry(
  () => import("../pages/SearchResults/SearchResults")
);
// spec-49 — the versions LIBRARY page (/versions). Named apart from the admin `Versions`
// (pages/manage/Versions) already imported above, which owns /admin/versions.
const VersionsLibrary: FC = lazyWithRetry(
  () => import("../pages/Versions/Versions")
);
const Test: FC = lazyWithRetry(() => import("../pages/Test/Test"));
const PostProductionPriceCalc: FC = lazyWithRetry(
  () => import("../features/NewPrices2024/pages/PostProductionPriceCalc")
);
// Deliberately NOT annotated `FC`: a bare FC narrows props to {}, so the `report` prop each of the
// three spec-50 routes passes would be a type error. Same trap spec 46 hit with VideoReport.
const PostProductionReportPageWrapper = lazyWithRetry(
  () => import("../features/NewPrices2024/pages/PostProductionReportPageWrapper")
);

//@ts-ignore
const Suspense: FC<any> = (props) => (
  <ReactSuspense fallback={props.fallback}>{props.children}</ReactSuspense>
);

export type PrivateRouteT = {
  path: RoutesE;
  element: JSX.Element;
  role?: UserRoleE[];
  /**
   * Access levels allowed, checked IN ADDITION to `role`. Omit for no access-level gate.
   *
   * This exists because `role` alone cannot express "admins only": getUserRole discards the
   * access level for ordinary client users and returns the composite client-type string, which
   * is byte-identical for an Admin, a User and a Guest of the same client type.
   */
  accessLevel?: AccessLevelE[];
  /**
   * Where to send a user who passes `role` but fails `accessLevel`. Without it the route is not
   * registered at all, which falls through to the `*` entry and lands on `/` — safe, but not the
   * intended destination.
   */
  redirectTo?: RoutesE;
};

// uses when user is logged in
export const privateRoutes: PrivateRouteT[] = [
  // main section
  {
    path: RoutesE.TEST,
    element: <Test />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.Broadcaster,
    ],
  },
  {
    path: RoutesE.HOME,
    element: <Navigate to={RoutesE.CAMPAIGNS} />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.HOME,
    element: <Navigate to={RoutesE.DELIVERIES} />,
    role: [UserRoleE.Broadcaster],
  },
  {
    path: RoutesE.PROJECTS,
    element: <Navigate to={RoutesE.DELIVERIES} />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.HOME,
    element: <Navigate to={RoutesE.HOME_DELIVERY} />,
    role: [UserRoleE.DeliveryService],
  },
  // Privacy guard: delivery-service users (JumboDelivery / JumboDeliveryEng) must
  // not reach the cross-publisher campaign/delivery lists. These same-path entries
  // are scoped to [DeliveryService] only (disjoint from the page routes below), so
  // for those users the list pages are replaced by a redirect to their own landing.
  {
    path: RoutesE.CAMPAIGNS,
    element: <Navigate to={RoutesE.HOME_DELIVERY} />,
    role: [UserRoleE.DeliveryService],
  },
  {
    path: RoutesE.DELIVERIES,
    element: <Navigate to={RoutesE.HOME_DELIVERY} />,
    role: [UserRoleE.DeliveryService],
  },
  {
    path: RoutesE.PROJECTS,
    element: <Navigate to={RoutesE.HOME_DELIVERY} />,
    role: [UserRoleE.DeliveryService],
  },
  {
    path: RoutesE.PROJECT_ORDER_FINISHED,
    element: <PaymentSuccess />,
    role: [UserRoleE.DeliveryService, UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.RE_DELIVERY_CONFIRMATION,
    element: (
      <Suspense fallback={<Loader />}>
        <ReDeliveryConfirmation />
      </Suspense>
    ),
    role: [
      UserRoleE.AdvertisingAgency,
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
    ],
  },
  {
    path: RoutesE.RE_DELIVERY_VERSIONS,
    element: (
      <Suspense fallback={<Loader />}>
        <ReDeliveryVersions />
      </Suspense>
    ),
    role: [
      UserRoleE.AdvertisingAgency,
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
    ],
  },
  {
    path: RoutesE.RE_DELIVERY_PAYMENT,
    element: (
      <Suspense fallback={<Loader />}>
        <ReDeliveryOrderPayment />
      </Suspense>
    ),
    role: [
      UserRoleE.AdvertisingAgency,
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
    ],
  },

  {
    path: RoutesE.CAMPAIGNS,
    element: <CampaignsList />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.Broadcaster,
      // DeliveryService intentionally excluded — redirected to HOME_DELIVERY above
      UserRoleE.MediaCompany,
    ],
  },

  // projects section
  {
    path: RoutesE.DELIVERIES,
    element: <ProjectList />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.Broadcaster,
      // DeliveryService intentionally excluded — redirected to HOME_DELIVERY above
      UserRoleE.MediaCompany,
    ],
  },
  {
    // spec-49 D6 — the versions library; same audience as Deliveries, DeliveryService excluded.
    path: RoutesE.VERSIONS,
    element: (
      <Suspense fallback={<Loader />}>
        <VersionsLibrary />
      </Suspense>
    ),
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  // {
  //   path: "/projects/editVideoAd/:projectId",
  //   element: <EditVideoProject />,
  //   role: [
  //     UserRoleE.DeliveryService,
  //     UserRoleE.GlobalAdmin,
  //     UserRoleE.PostProduction,
  //     UserRoleE.AdvertisingAgency,
  //   ],
  // },
  {
    path: RoutesE.DELIVERY,
    element: <ViewVideoProject />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.PROJECT_VIEW,
    element: <ViewVideoProjectRedirect />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  // {
  //   path: "/projects/create/videoAd",
  //   element: <CreateVideoProject />,
  //   role: [
  //     UserRoleE.DeliveryService,
  //     UserRoleE.GlobalAdmin,
  //     UserRoleE.PostProduction,
  //     UserRoleE.AdvertisingAgency,
  //   ],
  // },

  {
    path: RoutesE.PROJECT_SHARED,
    element: <ShareVideo />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },

  {
    path: RoutesE.CAMPAIGN_NEW,
    element: <Campaign />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.CAMPAIGN,
    element: <CampaignRedirect />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },

  // delivery section
  {
    path: RoutesE.HOME_DELIVERY,
    element: <DeliveryServiceIndex />,
    role: [
      UserRoleE.DeliveryService,
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
    ],
  },
  {
    path: RoutesE.PROJECT_PAYMENT,
    element: <OrderPayment />,
    role: [UserRoleE.DeliveryService, UserRoleE.GlobalAdmin],
  },

  // manage section
  {
    path: RoutesE.MANAGE_CLIENTS,
    element: (
      <Suspense fallback={<Loader />}>
        <Clients />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.REGISTRATION,
    element: <Registration />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_CLIENTS_ID,
    element: <ClientPage />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_FTP_SETTINGS,
    element: (
      <Suspense fallback={<Loader />}>
        <FTPSettings />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_SERVICE_PACKAGES,
    element: (
      <Suspense fallback={<Loader />}>
        <ServicePackages />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_NEW_SERVICE_PACKAGES,
    element: (
      <Suspense fallback={<Loader />}>
        <NewServicePackages />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_VIDEO_GUIDES,
    element: (
      <Suspense fallback={<Loader />}>
        <VideoGuides />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_GLOBAL_SETTINGS,
    element: (
      <Suspense fallback={<Loader />}>
        <ManageGlobalSettings />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_USERS,
    element: (
      <Suspense fallback={<Loader />}>
        <Users />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_CAMPAIGNS,
    element: (
      <Suspense fallback={<Loader />}>
        <Campaigns />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_PROJECTS,
    element: (
      <Suspense fallback={<Loader />}>
        <Projects />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_PRODUCTION_COMPANIES,
    element: (
      <Suspense fallback={<Loader />}>
        <ProductionCompanies />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_PROJECT_FILES,
    element: (
      <Suspense fallback={<Loader />}>
        <Versions />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_PROJECT_FILES_OLD,
    element: <Navigate to={RoutesE.MANAGE_PROJECT_FILES} />,
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_ADVERTISERS,
    element: (
      <Suspense fallback={<Loader />}>
        <Advertisers />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_BRANDS,
    element: (
      <Suspense fallback={<Loader />}>
        <Brands />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_MARKET_CATEGORIES,
    element: (
      <Suspense fallback={<Loader />}>
        <Industry />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_NEWSLETTERS,
    element: (
      <Suspense fallback={<Loader />}>
        <Mails />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_SYSTEM_MAILS_OLD,
    element: <Navigate to={RoutesE.MANAGE_NEWSLETTERS} />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_MAIL_TEMPLATES,
    element: (
      <Suspense fallback={<Loader />}>
        <MailTemplates />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_SUPPORT_MESSAGES,
    element: (
      <Suspense fallback={<Loader />}>
        <SupportMessages />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_SUPPORT_MESSAGES_OLD,
    element: <Navigate to={RoutesE.MANAGE_SUPPORT_MESSAGES} />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_DELIVERY_ORDERS,
    element: (
      <Suspense fallback={<Loader />}>
        <DeliveryOrders />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_RE_DELIVERY_ORDERS,
    element: (
      <Suspense fallback={<Loader />}>
        <ReDeliveryOrders />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_HOUSE_NUMBERS,
    element: (
      <Suspense fallback={<Loader />}>
        <ProjectFileBroadcasters />
      </Suspense>
    ),
    role: [UserRoleE.GlobalAdmin, UserRoleE.GlobalAdminAndAdminContentManager],
  },
  {
    path: RoutesE.MANAGE_DELIVERY_ORDERS_OLD,
    element: <Navigate to={RoutesE.MANAGE_DELIVERY_ORDERS} />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_TRANSLATES,
    element: <Translations />,
    role: [UserRoleE.GlobalAdmin],
  },

  // reports section — one route per report (spec 46); the old single URL redirects.
  {
    path: RoutesE.REPORT_VIDEO_OLD,
    element: <LegacyVideoReportRedirect />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.REPORT_GENERAL,
    element: <VideoReport report="generalReport" />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.REPORT_CLIENT,
    element: <VideoReport report="clientReport" />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.REPORT_BROADCASTERS_INNER,
    element: <VideoReport report="broadcastersInnerReport" />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.REPORT_AUDIO,
    element: <AudioReport />,
    role: [UserRoleE.GlobalAdmin],
  },

  // settings section
  {
    path: RoutesE.SETTINGS,
    element: <SettingsRouteEntry />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SETTINGS_USER,
    element: <UserSettings />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SETTINGS_NOTIFICATIONS,
    element: <NotificationSettings />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SETTINGS_PASSWORD,
    element: <PasswordSettings />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SETTINGS_COMPANY,
    element: <CompanyProfileSettings />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SETTINGS_FTP,
    element: <FTPSettingsBroadcaster />,
    role: [UserRoleE.Broadcaster],
  },
  {
    path: RoutesE.SETTINGS_USERS,
    element: <UserManagement />,
    // Account administration: a client's own users. `accessLevel` is the only thing that actually
    // gates this page — the role list is deliberately every non-GlobalAdmin client-type composite
    // so that activating a client type needs no edit here, matching JMRoles.ClientAdmins on the
    // backend. GlobalAdmin stays out: platform admins manage users at /admin/users.
    // NOTE: RadioStation is listed but unreachable today — mapClientTypeToRole has no `case 4`, so
    // type-4 users resolve to GlobalAdmin. Listing it now means that fix needs no change here.
    role: [
      UserRoleE.AdvertisingAgency,
      UserRoleE.PostProduction,
      UserRoleE.Broadcaster,
      UserRoleE.RadioStation,
      UserRoleE.MediaCompany,
      UserRoleE.DeliveryService,
      UserRoleE.Production,
    ],
    accessLevel: [AccessLevelE.Admin],
    redirectTo: RoutesE.SETTINGS_USER,
  },
  {
    path: RoutesE.SETTINGS_RECIPIENTS,
    element: <RecipientsManagement />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.MediaCompany,
    ],
  },

  // support section
  {
    path: RoutesE.MANAGE_SUPPORT_MESSAGES,
    element: <SupportMessages />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.SUPPORT,
    element: <Support />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SUPPORT_CONTACT_US,
    element: <Support />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SUPPORT_VIDEO_GUIDES,
    element: <SupportVideoGuides />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.SUPPORT_TECHNICAL_INSTRUCTION,
    element: <SupportTechInstruction />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.POST_PRODUCTION_PRICE_CALC,
    element: <PostProductionPriceCalc />,
    role: [UserRoleE.PostProduction, UserRoleE.DeliveryService],
  },
  {
    path: RoutesE.TERMS,
    element: <Terms />,
  },
  {
    path: RoutesE.PRIVACY_POLICY,
    element: <PrivacyPolicy />,
  },
  {
    path: RoutesE.HELP,
    element: <HelpMobile />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.MANAGE_LEADS,
    element: <Leads />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.MANAGE_LEADS_OLD,
    element: <Navigate to={RoutesE.MANAGE_LEADS} />,
    role: [UserRoleE.GlobalAdmin],
  },
  {
    path: RoutesE.FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
  {
    path: RoutesE.RESET_PASSWORD,
    element: <ResetPassword />,
  },
  {
    path: RoutesE.SEARCH_RESULTS,
    element: <SearchResults />,
  },
  {
    path: RoutesE.REPORTS,
    element: <PostProductionReportPageWrapper report="monthly" />,
    role: [
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.REPORTS_ANNUAL,
    element: <PostProductionReportPageWrapper report="annual" />,
    role: [
      UserRoleE.PostProduction,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.MediaCompany,
    ],
  },
  {
    // spec 50 D2 — only post-production clients have billing rows; the other three roles would
    // always see an empty table, so the route does not exist for them.
    path: RoutesE.REPORTS_ACTIVITY_SUMMARY,
    element: <PostProductionReportPageWrapper report="activitySummary" />,
    role: [UserRoleE.PostProduction],
  },

  // default
  {
    path: RoutesE.DEFAULT,
    // element: <NotFound />,
    element: <Navigate to={RoutesE.HOME} />,
    role: [
      UserRoleE.GlobalAdmin,
      UserRoleE.GlobalAdminAndAdminContentManager,
      UserRoleE.AdvertisingAgency,
      UserRoleE.Broadcaster,
      UserRoleE.PostProduction,
      UserRoleE.Production,
      UserRoleE.DeliveryService,
      UserRoleE.MediaCompany,
    ],
  },
  {
    path: RoutesE.PROJECT_SHARED_AD,
    element: <ShareVideoAd />,
  },
];
